import { Button, EButtonColor } from '@web_opxp/components';
import { useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { SKELETON_CLASSNAME } from '../base/constants';
import { useNavigationBlocker } from '../medBanner';
import './ProductItem.scss';
import { IProductData } from './types';
import * as Utils from './utils';

export interface IProductItemProps {
  productData: IProductData;
  language?: string;
  onClick: (productId: number) => void;
}

export const ProductItem: React.FC<IProductItemProps> = ({ productData, language, onClick }: IProductItemProps) => {
  const { t } = useTranslation();
  const { unblockNavigation } = useNavigationBlocker();
  const isLoading = !language || !productData.normalPrice;
  const skeletonClass = isLoading ? SKELETON_CLASSNAME : '';
  const loadingClass = isLoading ? 'is-loading' : '';

  const periodLabel = Utils.isProductOneTimePayment(productData)
    ? t('Subscribe.Period.Onetime')
    : t('Subscribe.Period.Monthly');

  const doesHaveFreeTrial = Utils.hasProductFreeTrial(productData);
  const isFeatured = doesHaveFreeTrial || Utils.isProductRecommended(productData);
  const featuredTitle = doesHaveFreeTrial ? t('Subscribe.Trial.Featured') : t('Subscribe.Featured');
  const featuredLine = isFeatured ? <span className="product-featured-banner">{featuredTitle}</span> : null;
  const actionLabel = doesHaveFreeTrial ? t('Subscribe.Trial.Action') : t('Subscribe.Action');
  const clickHandler = () => {
    if (isLoading) {
      // prevent selecting product in loading state
      return;
    }
    // unblock navigation if it was blocked by med banner
    unblockNavigation();
    // select product and go to checkout
    onClick(Utils.getProductId(productData));
  };

  const productTitle = Utils.getProductTranslatedTitle(productData, t);
  const discounted = Utils.isProductDiscounted(productData);
  const productCurrency = Utils.getProductCurrency(productData);
  const productPrice = Utils.getProductPrice(productData);
  const oldPriceText = Utils.formatCurrency(productPrice, productCurrency, language);
  const discountedPrice = Utils.getProductDiscountedPrice(productData);
  const actualPriceText = Utils.formatCurrency(discountedPrice, productCurrency, language);
  const discountPercentage = Utils.getDiscountPercentage(productData);
  const buttonColor = isFeatured ? EButtonColor.green : EButtonColor.blue;
  const productItemClasses = ['product-item', isFeatured ? 'product-featured' : '', loadingClass].join(' ');

  return (
    <div className={productItemClasses} onClick={clickHandler}>
      {featuredLine}
      <div className="product">
        <p className={`product-title ${skeletonClass}`}>{productTitle}</p>
        <div className={`price-container ${discounted ? 'discounted' : 'not-discounted'}`}>
          {discounted ? (
            <>
              <div className="discount-percentage">{`-${discountPercentage}%`}</div>
              <h3 className={`product-oldprice ${skeletonClass}`}>
                {oldPriceText}
                <span className="score-out"></span>
              </h3>
            </>
          ) : null}
          <p className={`product-price nn-h1 ${skeletonClass}`}>
            {actualPriceText}
            <small className={`product-subtext  ${skeletonClass}`}>{periodLabel}</small>
          </p>
        </div>
        <div className="product-button-container">
          <Button className={`product-button ${skeletonClass}`} color={buttonColor} label={actionLabel} />
        </div>
      </div>
    </div>
  );
};

export default ProductItem;
